import { axiosInstance } from 'src/constants/axiosInstance';
import { ApiResponse } from 'src/types/commonResponseTypes';
import {
  ActionAuditItem,
  ActionCommandResponse,
  ActionMutationResponse,
  CreateManualActionInput,
  ActionEvidenceItem,
  ActionDuplicateComparison,
  ActionReviewCapability,
  ActionReviewItem,
  ActionReviewPageResponse,
  ActionReviewView,
  ActionReviewScope,
  ActionReviewDueFilter,
  ActionReviewSort,
  BetterMindWorkflowKind,
  BetterMindWorkflowResult,
  ActionRevisionCommand,
  DismissActionCommand,
  DuplicateActionCommand,
  ActionValueSummary,
  BetterMindRoutingDirectoryPage,
  BetterMindRoutingRecord,
  BetterMindQuickCaptureBatchItem,
  BetterMindQuickCaptureBatchResult,
  BetterMindQuickCaptureCandidate,
  BetterMindDocumentationSummary,
  BetterMindCareOutcomesSummary,
  BetterMindCareQueueFilter,
  BetterMindOperationalQueue,
  BetterMindCarePlaybook,
  BetterMindPlaybookChangeRequest,
  BetterMindPlaybookId,
  UpdateActionInput,
  ActionChangeFeedResponse,
} from 'src/types/actionReview';
import { getApiErrorDetails } from 'src/utils/apiErrorUtils';

const ACTIONS_ROUTE = '/v1/actions';

export interface ActionApiError {
  code: string | null;
  message: string;
  status: number | null;
}

export const getActionReviewCapability = async () => {
  const { data } = await axiosInstance.get<ApiResponse<ActionReviewCapability>>(
    `${ACTIONS_ROUTE}/capability`,
  );

  return data;
};

export const getActionValueSummary = async () => {
  const { data } = await axiosInstance.get<ApiResponse<ActionValueSummary>>(
    `${ACTIONS_ROUTE}/value-summary`,
  );

  return data;
};

export const getActionChangeFeed = async (limit = 8) => {
  const { data } = await axiosInstance.get<
    ApiResponse<ActionChangeFeedResponse>
  >(`${ACTIONS_ROUTE}/changes?limit=${encodeURIComponent(String(limit))}`);

  return data;
};

export const undoActionChange = async (
  reconciliationId: string,
  revision: number,
  idempotencyKey: string,
) => {
  const { data } = await axiosInstance.post<
    ApiResponse<ActionMutationResponse>
  >(
    `${ACTIONS_ROUTE}/changes/${encodeURIComponent(reconciliationId)}/undo`,
    { revision, idempotencyKey },
    { headers: { 'x-correlation-id': idempotencyKey } },
  );

  return data;
};

const careMutationHeaders = (idempotencyKey: string) => ({
  'Idempotency-Key': idempotencyKey,
});

export const createBetterMindWorkflow = async (
  payload: {
    workflowKind: BetterMindWorkflowKind;
    operationalQueue: BetterMindOperationalQueue;
    routingRecordId?: string;
    caseReference: string;
    requestSummary: string;
    intakeSource: string;
    accountableOwnerId: string;
    providerReviewerId: string;
    nextReviewAt: string;
  },
  idempotencyKey: string,
) => {
  const { data } = await axiosInstance.post<
    ApiResponse<BetterMindWorkflowResult>
  >(`${ACTIONS_ROUTE}/care-operations`, payload, {
    headers: careMutationHeaders(idempotencyKey),
  });

  return data;
};

export const previewBetterMindQuickCapture = async (note: string) => {
  const { data } = await axiosInstance.post<
    ApiResponse<{ candidates: BetterMindQuickCaptureCandidate[] }>
  >(`${ACTIONS_ROUTE}/care-operations/quick-capture/preview`, { note });

  return data;
};

export const transcribeBetterMindQuickCapture = async (file: File) => {
  const formData = new FormData();

  formData.append('file', file);
  const { data } = await axiosInstance.post<ApiResponse<{ text: string }>>(
    `${ACTIONS_ROUTE}/care-operations/quick-capture/transcribe`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );

  return data;
};

export const createBetterMindQuickCapture = async (
  items: BetterMindQuickCaptureBatchItem[],
  idempotencyKey: string,
) => {
  const { data } = await axiosInstance.post<
    ApiResponse<BetterMindQuickCaptureBatchResult>
  >(
    `${ACTIONS_ROUTE}/care-operations/quick-capture`,
    { items },
    { headers: careMutationHeaders(idempotencyKey) },
  );

  return data;
};

export const getBetterMindRoutingDirectory = async (params: {
  query?: string;
  page?: number;
  limit?: number;
  includeArchived?: boolean;
}) => {
  const query = new URLSearchParams();

  if (params.query?.trim()) query.set('query', params.query.trim());
  if (params.page) query.set('page', String(params.page));
  if (params.limit) query.set('limit', String(params.limit));
  if (params.includeArchived) query.set('includeArchived', 'true');
  const suffix = query.toString() ? `?${query.toString()}` : '';
  const { data } = await axiosInstance.get<
    ApiResponse<BetterMindRoutingDirectoryPage>
  >(`${ACTIONS_ROUTE}/care-operations/routing-directory${suffix}`);

  return data;
};

export const createBetterMindRoutingRecord = async (
  payload: {
    displayName: string;
    role: string;
    patientReference: string | null;
    contactLookupHint: string | null;
  },
  idempotencyKey: string,
) => {
  const { data } = await axiosInstance.post<
    ApiResponse<BetterMindRoutingRecord>
  >(`${ACTIONS_ROUTE}/care-operations/routing-directory`, payload, {
    headers: careMutationHeaders(idempotencyKey),
  });

  return data;
};

export const updateBetterMindRoutingRecord = async (
  recordId: string,
  payload: {
    displayName: string;
    role: string;
    patientReference: string | null;
    contactLookupHint: string | null;
    expectedRevision: number;
  },
) => {
  const { data } = await axiosInstance.patch<
    ApiResponse<BetterMindRoutingRecord>
  >(
    `${ACTIONS_ROUTE}/care-operations/routing-directory/${encodeURIComponent(recordId)}`,
    payload,
  );

  return data;
};

export const archiveBetterMindRoutingRecord = async (
  recordId: string,
  expectedRevision: number,
) => {
  const { data } = await axiosInstance.post<
    ApiResponse<BetterMindRoutingRecord>
  >(
    `${ACTIONS_ROUTE}/care-operations/routing-directory/${encodeURIComponent(recordId)}/archive`,
    { expectedRevision },
  );

  return data;
};

export const restoreBetterMindRoutingRecord = async (
  recordId: string,
  expectedRevision: number,
) => {
  const { data } = await axiosInstance.post<
    ApiResponse<BetterMindRoutingRecord>
  >(
    `${ACTIONS_ROUTE}/care-operations/routing-directory/${encodeURIComponent(recordId)}/restore`,
    { expectedRevision },
  );

  return data;
};

export const applyBetterMindWorkflowCommand = async (
  taskId: string,
  payload: {
    command:
      | 'complete_step'
      | 'run_documentation_preflight'
      | 'confirm_handoff'
      | 'reopen';
    expectedRevision: number;
    stepKey?: string;
    externalReference?: string;
    documentationChecks?: {
      patientEncounterMatched: true;
      approvedInstructionSetApplied: true;
      requiredSectionsPresent: true;
    };
  },
  idempotencyKey: string,
) => {
  const { data } = await axiosInstance.post<
    ApiResponse<BetterMindWorkflowResult>
  >(
    `${ACTIONS_ROUTE}/care-operations/${encodeURIComponent(taskId)}/commands`,
    payload,
    { headers: careMutationHeaders(idempotencyKey) },
  );

  return data;
};

export const getBetterMindDocumentationSummary = async () => {
  const { data } = await axiosInstance.get<
    ApiResponse<BetterMindDocumentationSummary>
  >(`${ACTIONS_ROUTE}/care-operations/documentation-summary`);

  return data;
};

export const getBetterMindCareOutcomes = async () => {
  const { data } = await axiosInstance.get<
    ApiResponse<BetterMindCareOutcomesSummary>
  >(`${ACTIONS_ROUTE}/care-operations/outcomes`);

  return data;
};

export const getBetterMindCarePlaybooks = async () => {
  const { data } = await axiosInstance.get<
    ApiResponse<BetterMindCarePlaybook[]>
  >(`${ACTIONS_ROUTE}/care-operations/playbooks`);

  return data;
};

export const requestBetterMindPlaybookChange = async (
  playbookId: BetterMindPlaybookId,
  requestText: string,
  idempotencyKey: string,
) => {
  const { data } = await axiosInstance.post<
    ApiResponse<BetterMindPlaybookChangeRequest>
  >(
    `${ACTIONS_ROUTE}/care-operations/playbooks/${encodeURIComponent(playbookId)}/change-requests`,
    { requestText },
    { headers: careMutationHeaders(idempotencyKey) },
  );

  return data;
};

export const getReviewedActions = async (params: {
  page?: number;
  limit?: number;
  query?: string;
  states?: string[];
  view?: ActionReviewView;
  scope?: ActionReviewScope;
  priority?: string;
  due?: ActionReviewDueFilter;
  sort?: ActionReviewSort;
  clientKey?: 'abettermindset-health';
  workflowKind?: BetterMindWorkflowKind;
  careQueue?: BetterMindCareQueueFilter;
  sourceId?: string;
  firstValueSessionId?: string;
  includeCounts?: boolean;
  includeScopeCounts?: boolean;
  includeStatusCounts?: boolean;
}) => {
  const query = new URLSearchParams();

  if (params.page) query.set('page', String(params.page));
  if (params.limit) query.set('limit', String(params.limit));
  if (params.query?.trim()) query.set('query', params.query.trim());
  params.states?.forEach((state) => query.append('states', state));
  if (params.view) query.set('view', params.view);
  if (params.scope) query.set('scope', params.scope);
  if (params.priority) query.set('priority', params.priority);
  if (params.due) query.set('due', params.due);
  if (params.sort) query.set('sort', params.sort);
  if (params.clientKey) query.set('clientKey', params.clientKey);
  if (params.workflowKind) query.set('workflowKind', params.workflowKind);
  if (params.careQueue) query.set('careQueue', params.careQueue);
  if (params.sourceId) query.set('sourceId', params.sourceId);

  if (params.firstValueSessionId) {
    query.set('firstValueSessionId', params.firstValueSessionId);
  }

  if (params.includeCounts !== undefined) {
    query.set('includeCounts', String(params.includeCounts));
  }

  if (params.includeScopeCounts !== undefined) {
    query.set('includeScopeCounts', String(params.includeScopeCounts));
  }

  if (params.includeStatusCounts !== undefined) {
    query.set('includeStatusCounts', String(params.includeStatusCounts));
  }

  const suffix = query.toString() ? `?${query.toString()}` : '';
  const { data } = await axiosInstance.get<
    ApiResponse<ActionReviewPageResponse>
  >(`${ACTIONS_ROUTE}${suffix}`);

  return data;
};

export const createManualAction = async (
  payload: CreateManualActionInput,
  idempotencyKey: string,
): Promise<ApiResponse<ActionMutationResponse>> => {
  const { data } = await axiosInstance.post<
    ApiResponse<ActionMutationResponse>
  >(`${ACTIONS_ROUTE}/manual`, payload, {
    headers: {
      'Idempotency-Key': idempotencyKey,
      'x-correlation-id': idempotencyKey,
    },
  });

  return data;
};

export const updateReviewedAction = async (
  actionId: string,
  payload: UpdateActionInput,
): Promise<ApiResponse<ActionMutationResponse>> => {
  const { data } = await axiosInstance.post<
    ApiResponse<ActionMutationResponse>
  >(`${ACTIONS_ROUTE}/${encodeURIComponent(actionId)}/update`, payload, {
    headers: { 'x-correlation-id': payload.idempotencyKey },
  });

  return data;
};

export const getReviewedAction = async (
  actionId: string,
  scope?: ActionReviewScope,
) => {
  const scopeQuery = scope ? `?scope=${encodeURIComponent(scope)}` : '';
  const { data } = await axiosInstance.get<ApiResponse<ActionReviewItem>>(
    `${ACTIONS_ROUTE}/${encodeURIComponent(actionId)}${scopeQuery}`,
  );

  return data;
};

export const getActionEvidence = async (actionId: string) => {
  const { data } = await axiosInstance.get<ApiResponse<ActionEvidenceItem[]>>(
    `${ACTIONS_ROUTE}/${encodeURIComponent(actionId)}/evidence`,
  );

  return data;
};

export const getActionAudit = async (actionId: string) => {
  const { data } = await axiosInstance.get<ApiResponse<ActionAuditItem[]>>(
    `${ACTIONS_ROUTE}/${encodeURIComponent(actionId)}/audit`,
  );

  return data;
};

export const getActionDuplicateComparison = async (signalId: string) => {
  const { data } = await axiosInstance.get<
    ApiResponse<ActionDuplicateComparison>
  >(`${ACTIONS_ROUTE}/${encodeURIComponent(signalId)}/duplicate-comparison`);

  return data;
};

export const acceptActionSuggestion = async (
  signalId: string,
  command: ActionRevisionCommand,
) => postCommand(`${encodeURIComponent(signalId)}/accept`, command);

export const dismissActionSuggestion = async (
  signalId: string,
  command: DismissActionCommand,
) => postCommand(`${encodeURIComponent(signalId)}/dismiss`, command);

export const mergeActionSuggestion = async (
  signalId: string,
  command: DuplicateActionCommand,
) => postCommand(`${encodeURIComponent(signalId)}/duplicates/merge`, command);

export const keepActionSuggestionSeparate = async (
  signalId: string,
  command: DuplicateActionCommand,
) =>
  postCommand(
    `${encodeURIComponent(signalId)}/duplicates/keep-separate`,
    command,
  );

const postCommand = async (
  route: string,
  command:
    | ActionRevisionCommand
    | DismissActionCommand
    | DuplicateActionCommand,
) => {
  const { data } = await axiosInstance.post<ApiResponse<ActionCommandResponse>>(
    `${ACTIONS_ROUTE}/${route}`,
    command,
    { headers: { 'x-correlation-id': command.idempotencyKey } },
  );

  return data;
};

export const normalizeActionApiError = (error: unknown): ActionApiError => {
  const details = getApiErrorDetails(
    error,
    'Something went wrong. Please try again.',
  );
  const code = typeof details.errorCode === 'string' ? details.errorCode : null;

  return {
    code,
    message: details.message,
    status: details.statusCode ?? null,
  };
};
